"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  createExpertsAliveLipSync,
  type AssetProgress,
  type ExpertsAliveLipSyncOptions,
  type ExpertsAliveLipSyncRuntime,
  type RuntimeEvent,
  type RuntimeSnapshot,
} from "../web/local/experts-alive-lipsync.js";
import type {
  LipSyncState,
  ResolutionMode,
  SdkPlatform,
  SubscriptionPlan,
} from "../runtime/lip-sync-contract.js";

export type DigitalHumanLifecycleState =
  | "cold"
  | "downloading"
  | "decoded"
  | "parked"
  | "attached"
  | "first-frame-ready"
  | "speaking"
  | "listening"
  | "error";

export type DigitalHumanPoolDiagnosticType =
  | "pool-hit"
  | "pool-miss"
  | "prepared"
  | "attached"
  | "detached"
  | "first-frame-painted"
  | "audio-bound"
  | "audio-unbound"
  | "speaking-replayed"
  | "evicted"
  | "destroyed"
  | "ownership-conflict"
  | "resolution-change";

export interface DigitalHumanPoolDiagnostic {
  type: DigitalHumanPoolDiagnosticType;
  at: number;
  key: string;
  groupKey?: string;
  generation: number;
  state: DigitalHumanLifecycleState;
  detail?: Record<string, unknown>;
}

export interface DigitalHumanPoolEntryStatus {
  key: string;
  groupKeys: string[];
  state: DigitalHumanLifecycleState;
  generation: number;
  attached: boolean;
  initialized: boolean;
  estimatedBytes: number;
  loadedBytes: number;
  totalBytes: number;
  lastUsedAt: number;
  snapshot: RuntimeSnapshot | null;
  error: string | null;
}

export interface DigitalHumanPoolStatus {
  entries: DigitalHumanPoolEntryStatus[];
  entryCount: number;
  estimatedBytes: number;
  attachedCount: number;
  downloadingCount: number;
}

export interface DigitalHumanPoolConfig {
  /** Maximum retained decoded runtimes. Defaults to 8. */
  maxEntries?: number;
  /** Optional approximate media budget. Entries should provide estimatedBytes. */
  maxEstimatedBytes?: number;
  onDiagnostic?: (event: DigitalHumanPoolDiagnostic) => void;
}

type PoolRuntimeOptionKeys =
  | "assetManifestUrl"
  | "settingsUrl"
  | "plan"
  | "bandwidthSaver"
  | "resolution"
  | "autoUpgradeWidth"
  | "autoDowngradeWidth"
  | "honorBrowserDataSaver"
  | "locale"
  | "platform"
  | "className";

export interface DigitalHumanPoolRequest extends Pick<ExpertsAliveLipSyncOptions, PoolRuntimeOptionKeys> {
  /** Stable Digital Human + manifest-version identity used by the pool. */
  key: string;
  /** Optional application-owned grouping used by disposeGroup(). */
  groupKey?: string;
  /** Approximate decoded/media footprint used by the optional memory budget. */
  estimatedBytes?: number;
}

export interface DigitalHumanPoolCallbacks {
  onError?: (error: Error) => void;
  onEvent?: (event: RuntimeEvent) => void;
  onInputLevel?: (level: number) => void;
  onProgress?: (progress: AssetProgress) => void;
  onResolutionChange?: ExpertsAliveLipSyncOptions["onResolutionChange"];
  onStateChange?: (state: LipSyncState, previous: LipSyncState) => void;
  onRuntimeDestroyed?: (runtime: ExpertsAliveLipSyncRuntime) => void;
}

export interface DigitalHumanRuntimeLease {
  readonly key: string;
  readonly generation: number;
  readonly runtime: ExpertsAliveLipSyncRuntime;
  readonly container: HTMLDivElement;
  readonly ready: Promise<RuntimeSnapshot>;
  readonly firstFrameReady: Promise<RuntimeSnapshot>;
  isCurrent: () => boolean;
  diagnose: (type: DigitalHumanPoolDiagnosticType, detail?: Record<string, unknown>) => void;
  release: (reason?: string) => void;
}

export interface PreloadGroupProgress {
  groupKey: string;
  completed: number;
  total: number;
  currentKey: string;
  status: DigitalHumanPoolEntryStatus;
}

interface PoolEntry {
  key: string;
  signature: string;
  groupKeys: Set<string>;
  container: HTMLDivElement;
  parkingSlot: HTMLDivElement;
  runtime: ExpertsAliveLipSyncRuntime;
  ready: Promise<RuntimeSnapshot>;
  snapshot: RuntimeSnapshot | null;
  state: DigitalHumanLifecycleState;
  owner: symbol | null;
  generation: number;
  callbacks: DigitalHumanPoolCallbacks | null;
  initialized: boolean;
  estimatedBytes: number;
  assetTotals: Map<string, number>;
  assetLoaded: Map<string, number>;
  lastUsedAt: number;
  error: Error | null;
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function stableSignature(request: DigitalHumanPoolRequest): string {
  return JSON.stringify({
    assetManifestUrl: request.assetManifestUrl,
    settingsUrl: request.settingsUrl,
    plan: request.plan ?? "free",
    bandwidthSaver: request.bandwidthSaver ?? false,
    resolution: request.resolution ?? "auto",
    autoUpgradeWidth: request.autoUpgradeWidth ?? 280,
    autoDowngradeWidth: request.autoDowngradeWidth ?? 240,
    honorBrowserDataSaver: request.honorBrowserDataSaver ?? true,
    locale: request.locale ?? "en-US",
    platform: request.platform ?? "webApp",
    className: request.className ?? "",
  });
}

function parkedWidth(request: DigitalHumanPoolRequest): number {
  if ((request.plan ?? "free") === "free" || request.bandwidthSaver || request.resolution === "small") return 210;
  return 360;
}

function statusFor(entry: PoolEntry): DigitalHumanPoolEntryStatus {
  const totalBytes = [...entry.assetTotals.values()].reduce((total, bytes) => total + bytes, 0);
  const loadedBytes = [...entry.assetLoaded.values()].reduce((total, bytes) => total + bytes, 0);
  return {
    key: entry.key,
    groupKeys: [...entry.groupKeys],
    state: entry.state,
    generation: entry.generation,
    attached: entry.owner !== null,
    initialized: entry.initialized,
    estimatedBytes: Math.max(entry.estimatedBytes, totalBytes),
    loadedBytes,
    totalBytes,
    lastUsedAt: entry.lastUsedAt,
    snapshot: entry.snapshot,
    error: entry.error?.message ?? null,
  };
}

/**
 * Owns decoded Digital Human runtimes independently of React screen lifetime.
 * A runtime has one exclusive lease at a time and is parked offscreen when its
 * current view unmounts.
 */
export class DigitalHumanRuntimePool {
  private readonly entries = new Map<string, PoolEntry>();
  private readonly subscribers = new Set<() => void>();
  private revision = 0;
  private config: Required<Pick<DigitalHumanPoolConfig, "maxEntries">> & DigitalHumanPoolConfig;
  private parkingRoot: HTMLDivElement | null = null;

  constructor(config: DigitalHumanPoolConfig = {}) {
    this.config = {
      ...config,
      maxEntries: Math.max(1, Math.floor(config.maxEntries ?? 8)),
    };
  }

  configure(config: DigitalHumanPoolConfig): void {
    this.config = {
      ...config,
      maxEntries: Math.max(1, Math.floor(config.maxEntries ?? 8)),
    };
    this.enforceLimits();
    this.notify();
  }

  subscribe(listener: () => void): () => void {
    this.subscribers.add(listener);
    return () => this.subscribers.delete(listener);
  }

  getRevision(): number {
    return this.revision;
  }

  getStatus(): DigitalHumanPoolStatus {
    const entries = [...this.entries.values()].map(statusFor);
    return {
      entries,
      entryCount: entries.length,
      estimatedBytes: entries.reduce((total, entry) => total + entry.estimatedBytes, 0),
      attachedCount: entries.filter((entry) => entry.attached).length,
      downloadingCount: entries.filter((entry) => entry.state === "downloading").length,
    };
  }

  async preloadGroup(
    groupKey: string,
    requests: readonly DigitalHumanPoolRequest[],
    onProgress?: (progress: PreloadGroupProgress) => void,
  ): Promise<DigitalHumanPoolEntryStatus[]> {
    if (!groupKey.trim()) throw new Error("preloadGroup() requires a stable groupKey.");
    const unique = [...new Map(requests.map((request) => [request.key, request])).values()];
    if (!unique.length) throw new Error("preloadGroup() requires at least one Digital Human.");
    if (unique.length > this.config.maxEntries) {
      throw new Error(`The group has ${unique.length} Digital Humans but maxEntries is ${this.config.maxEntries}.`);
    }
    const completed: DigitalHumanPoolEntryStatus[] = [];
    for (const request of unique) {
      const entry = this.getOrCreate({ ...request, groupKey });
      entry.groupKeys.add(groupKey);
      await entry.ready;
      const status = statusFor(entry);
      completed.push(status);
      onProgress?.({
        groupKey,
        completed: completed.length,
        total: unique.length,
        currentKey: request.key,
        status,
      });
    }
    this.enforceLimits(new Set(unique.map((request) => request.key)));
    return completed;
  }

  prepare(request: DigitalHumanPoolRequest): Pick<DigitalHumanRuntimeLease, "key" | "runtime" | "container" | "ready"> {
    const entry = this.getOrCreate(request);
    if (request.groupKey) entry.groupKeys.add(request.groupKey);
    entry.lastUsedAt = now();
    this.notify();
    return { key: entry.key, runtime: entry.runtime, container: entry.container, ready: entry.ready };
  }

  acquire(
    request: DigitalHumanPoolRequest,
    target: HTMLElement,
    callbacks: DigitalHumanPoolCallbacks = {},
  ): DigitalHumanRuntimeLease {
    const entry = this.getOrCreate(request);
    if (entry.owner) {
      this.emit(entry, "ownership-conflict");
      const error = new Error(
        `Digital Human pool key "${request.key}" already has an active owner. Use a different key for concurrent views.`,
      );
      if (typeof console !== "undefined") console.warn(`[Experts Alive] ${error.message}`);
      throw error;
    }
    if (request.groupKey) entry.groupKeys.add(request.groupKey);
    const owner = Symbol(request.key);
    const generation = ++entry.generation;
    entry.owner = owner;
    entry.callbacks = callbacks;
    entry.lastUsedAt = now();
    entry.runtime.resetForReuse("pool-acquire");
    target.replaceChildren(entry.container);
    entry.state = "attached";
    this.emit(entry, "attached");
    this.notify();

    const firstFrameReady = entry.ready
      .then(async (snapshot) => {
        if (!this.isCurrent(entry, owner, generation)) return snapshot;
        await entry.runtime.reevaluateViewSize(target.getBoundingClientRect().width);
        await this.waitForPaintedAttachment();
        if (!this.isCurrent(entry, owner, generation)) return snapshot;
        entry.state = "first-frame-ready";
        this.emit(entry, "first-frame-painted");
        this.notify();
        return snapshot;
      })
      .catch((cause) => { throw cause; });

    let released = false;
    return {
      key: entry.key,
      generation,
      runtime: entry.runtime,
      container: entry.container,
      ready: entry.ready,
      firstFrameReady,
      isCurrent: () => !released && this.isCurrent(entry, owner, generation),
      diagnose: (type, detail) => {
        if (!released && this.isCurrent(entry, owner, generation)) this.emit(entry, type, detail);
      },
      release: (reason = "react-unmount") => {
        if (released) return;
        released = true;
        this.releaseEntry(entry, owner, generation, reason);
      },
    };
  }

  release(lease: DigitalHumanRuntimeLease, reason = "host-release"): void {
    lease.release(reason);
  }

  disposeGroup(groupKey: string): void {
    for (const entry of [...this.entries.values()]) {
      if (!entry.groupKeys.delete(groupKey)) continue;
      if (entry.groupKeys.size === 0) this.destroyEntry(entry, "group-disposed");
    }
  }

  disposeAll(): void {
    for (const entry of [...this.entries.values()]) this.destroyEntry(entry, "pool-disposed");
    this.parkingRoot?.remove();
    this.parkingRoot = null;
  }

  private getOrCreate(request: DigitalHumanPoolRequest): PoolEntry {
    if (!request.key.trim()) throw new Error("A non-empty pool key is required.");
    if (typeof document === "undefined") throw new Error("The Digital Human pool requires a browser document.");
    const signature = stableSignature(request);
    const existing = this.entries.get(request.key);
    if (existing && existing.signature === signature && !existing.error) {
      this.emit(existing, "pool-hit");
      return existing;
    }
    if (existing) {
      if (existing.owner) {
        this.emit(existing, "ownership-conflict", { reason: "configuration-changed" });
        throw new Error(`Cannot change the configuration for attached pool key "${request.key}".`);
      }
      this.destroyEntry(existing, "configuration-changed");
    }

    const parkingSlot = document.createElement("div");
    const width = parkedWidth(request);
    parkingSlot.style.cssText = `height:${Math.round(width * 4 / 3)}px;width:${width}px;position:relative;overflow:hidden`;
    const container = document.createElement("div");
    container.dataset.eaPoolKey = request.key;
    container.style.cssText = "height:100%;width:100%;position:relative;overflow:hidden;aspect-ratio:3/4";
    parkingSlot.append(container);
    this.getParkingRoot().append(parkingSlot);

    const entry = {} as PoolEntry;
    Object.assign(entry, {
      key: request.key,
      signature,
      groupKeys: new Set(request.groupKey ? [request.groupKey] : []),
      container,
      parkingSlot,
      state: "cold" as DigitalHumanLifecycleState,
      owner: null,
      generation: 0,
      callbacks: null,
      initialized: false,
      estimatedBytes: Math.max(0, request.estimatedBytes ?? 0),
      assetTotals: new Map<string, number>(),
      assetLoaded: new Map<string, number>(),
      lastUsedAt: now(),
      snapshot: null,
      error: null,
    });

    const runtime = createExpertsAliveLipSync({
      assetManifestUrl: request.assetManifestUrl,
      settingsUrl: request.settingsUrl,
      plan: request.plan,
      bandwidthSaver: request.bandwidthSaver,
      resolution: request.resolution,
      autoUpgradeWidth: request.autoUpgradeWidth,
      autoDowngradeWidth: request.autoDowngradeWidth,
      honorBrowserDataSaver: request.honorBrowserDataSaver,
      locale: request.locale,
      platform: request.platform ?? "webApp",
      className: request.className,
      container,
      watermark: false,
      onError: (error) => {
        entry.error = error;
        entry.state = "error";
        entry.callbacks?.onError?.(error);
        this.notify();
      },
      onEvent: (event) => {
        if (event.type === "audio-attached") this.emit(entry, "audio-bound", event.detail);
        if (event.type === "audio-detached") this.emit(entry, "audio-unbound", event.detail);
        entry.callbacks?.onEvent?.(event);
      },
      onInputLevel: (level) => entry.callbacks?.onInputLevel?.(level),
      onProgress: (progress) => {
        entry.state = "downloading";
        entry.assetTotals.set(progress.asset, progress.totalBytes);
        entry.assetLoaded.set(progress.asset, progress.loadedBytes);
        entry.callbacks?.onProgress?.(progress);
        this.notify();
      },
      onResolutionChange: (next, previous) => {
        entry.callbacks?.onResolutionChange?.(next, previous);
        this.emit(entry, "resolution-change", { next, previous });
      },
      onStateChange: (state, previous) => {
        if (state === "speaking") entry.state = "speaking";
        else if (state === "listening") entry.state = "listening";
        else if (state === "error") entry.state = "error";
        else if (entry.owner && entry.initialized) entry.state = "first-frame-ready";
        entry.callbacks?.onStateChange?.(state, previous);
        this.notify();
      },
    });
    entry.runtime = runtime;
    entry.ready = runtime.initialize()
      .then((snapshot) => {
        entry.initialized = true;
        entry.snapshot = snapshot;
        entry.error = null;
        entry.state = entry.owner ? "attached" : "decoded";
        this.emit(entry, "prepared", { resolution: snapshot.resolution, humanId: snapshot.humanId });
        this.notify();
        this.enforceLimits(new Set([entry.key]));
        return snapshot;
      })
      .catch((cause) => {
        const error = cause instanceof Error ? cause : new Error(String(cause));
        entry.error = error;
        entry.state = "error";
        this.notify();
        throw error;
      });
    this.entries.set(entry.key, entry);
    this.emit(entry, "pool-miss");
    this.notify();
    this.enforceLimits(new Set([entry.key]));
    return entry;
  }

  private releaseEntry(entry: PoolEntry, owner: symbol, generation: number, reason: string): void {
    if (!this.isCurrent(entry, owner, generation)) return;
    entry.runtime.resetForReuse(reason);
    entry.callbacks = null;
    entry.owner = null;
    entry.lastUsedAt = now();
    entry.parkingSlot.replaceChildren(entry.container);
    entry.state = entry.initialized ? "parked" : "downloading";
    this.emit(entry, "detached", { reason });
    this.notify();
    this.enforceLimits();
  }

  private destroyEntry(entry: PoolEntry, reason: string): void {
    if (this.entries.get(entry.key) !== entry) return;
    const callbacks = entry.callbacks;
    entry.generation += 1;
    entry.owner = null;
    entry.callbacks = null;
    this.entries.delete(entry.key);
    entry.runtime.destroy();
    callbacks?.onRuntimeDestroyed?.(entry.runtime);
    entry.parkingSlot.remove();
    this.emit(entry, reason === "lru" || reason === "memory-budget" ? "evicted" : "destroyed", { reason });
    this.notify();
  }

  private enforceLimits(protectedKeys = new Set<string>()): void {
    const maxBytes = this.config.maxEstimatedBytes;
    while (
      this.entries.size > this.config.maxEntries
      || (maxBytes !== undefined && this.getStatus().estimatedBytes > maxBytes)
    ) {
      const candidate = [...this.entries.values()]
        .filter((entry) => !entry.owner && !protectedKeys.has(entry.key) && (entry.initialized || entry.error))
        .sort((left, right) => left.lastUsedAt - right.lastUsedAt)[0];
      if (!candidate) break;
      this.destroyEntry(
        candidate,
        this.entries.size > this.config.maxEntries ? "lru" : "memory-budget",
      );
    }
  }

  private isCurrent(entry: PoolEntry, owner: symbol, generation: number): boolean {
    return this.entries.get(entry.key) === entry
      && entry.owner === owner
      && entry.generation === generation;
  }

  private getParkingRoot(): HTMLDivElement {
    if (this.parkingRoot?.isConnected) return this.parkingRoot;
    const root = document.createElement("div");
    root.dataset.eaRuntimeParking = "true";
    root.setAttribute("aria-hidden", "true");
    root.style.cssText = [
      "position:fixed",
      "left:-20000px",
      "top:0",
      "overflow:hidden",
      "pointer-events:none",
      "visibility:hidden",
      "contain:strict",
    ].join(";");
    document.body.append(root);
    this.parkingRoot = root;
    return root;
  }

  private waitForPaintedAttachment(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  }

  private emit(
    entry: PoolEntry,
    type: DigitalHumanPoolDiagnosticType,
    detail?: Record<string, unknown>,
  ): void {
    this.config.onDiagnostic?.({
      type,
      at: now(),
      key: entry.key,
      groupKey: [...entry.groupKeys][0],
      generation: entry.generation,
      state: entry.state,
      detail,
    });
  }

  private notify(): void {
    this.revision += 1;
    for (const subscriber of this.subscribers) subscriber();
  }
}

const DigitalHumanPoolContext = createContext<DigitalHumanRuntimePool | null>(null);

export interface ExpertsAliveProviderProps {
  children: ReactNode;
  pool?: DigitalHumanPoolConfig;
}

export function ExpertsAliveProvider({ children, pool: config = {} }: ExpertsAliveProviderProps) {
  const [runtimePool] = useState(() => new DigitalHumanRuntimePool(config));
  const { maxEntries, maxEstimatedBytes, onDiagnostic } = config;

  useEffect(() => {
    runtimePool.configure({ maxEntries, maxEstimatedBytes, onDiagnostic });
  }, [maxEntries, maxEstimatedBytes, onDiagnostic, runtimePool]);

  useEffect(() => () => runtimePool.disposeAll(), [runtimePool]);

  return (
    <DigitalHumanPoolContext.Provider value={runtimePool}>
      {children}
    </DigitalHumanPoolContext.Provider>
  );
}

export const DigitalHumanPoolProvider = ExpertsAliveProvider;

export function useExpertsAlivePool(): DigitalHumanRuntimePool {
  const pool = useContext(DigitalHumanPoolContext);
  if (!pool) throw new Error("useExpertsAlivePool() must be used inside ExpertsAliveProvider.");
  return pool;
}

export function useOptionalExpertsAlivePool(): DigitalHumanRuntimePool | null {
  return useContext(DigitalHumanPoolContext);
}

export function useDigitalHumanPoolStatus(): DigitalHumanPoolStatus {
  const pool = useExpertsAlivePool();
  useSyncExternalStore(
    (listener) => pool.subscribe(listener),
    () => pool.getRevision(),
    () => 0,
  );
  return pool.getStatus();
}

let legacyPool: DigitalHumanRuntimePool | null = null;

/** Backward-compatible pool used only by LocalDigitalHuman.persistentKey. */
export function getLegacyDigitalHumanPool(): DigitalHumanRuntimePool {
  if (!legacyPool) legacyPool = new DigitalHumanRuntimePool({ maxEntries: 8 });
  return legacyPool;
}

/** Utility for application-owned preload requests. */
export function createDigitalHumanPoolRequest(options: {
  key: string;
  assetManifestUrl: string;
  settingsUrl: string;
  groupKey?: string;
  estimatedBytes?: number;
  plan?: SubscriptionPlan;
  resolution?: ResolutionMode;
  locale?: string;
  platform?: SdkPlatform;
}): DigitalHumanPoolRequest {
  return { ...options };
}
